/**
 * Rule Suggestion Reviewer - UI for LLM-Assisted Rule Creation (Epic #568 Phase 6)
 *
 * Provides an interface for administrators to:
 * - Generate rule suggestions using Claude AI via AWS Bedrock
 * - Review pending suggestions with confidence scores
 * - Accept suggestions to convert to active rules
 * - Reject suggestions with reasoning
 * - Get detailed AI explanations for suggestions
 */

import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Code,
  Collapse,
  Divider,
  Group,
  Loader,
  Modal,
  MultiSelect,
  NumberInput,
  Paper,
  Progress,
  ScrollArea,
  Select,
  SimpleGrid,
  Stack,
  Tabs,
  Text,
  Textarea,
  Title,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconBrain,
  IconCheck,
  IconChevronDown,
  IconChevronUp,
  IconInfoCircle,
  IconRefresh,
  IconRobot,
  IconSparkles,
  IconTrash,
  IconX,
} from '@tabler/icons-react';
import React, { useCallback, useState } from 'react';
import {
  formatImpact,
  getConfidenceColor,
  SuggestionCategory,
  type SuggestedRule,
  type SuggestionContextInput,
  useSuggestionReview,
} from '../hooks/useRuleSuggestions';

// ============================================================================
// TYPES
// ============================================================================

interface RuleSuggestionReviewerProps {
  tenantId: string;
  onRuleAccepted?: (rule: SuggestedRule) => void;
  onRuleRejected?: (rule: SuggestedRule) => void;
}

interface SuggestionCardProps {
  suggestion: SuggestedRule;
  onAccept: () => Promise<void>;
  onReject: () => Promise<void>;
  onExplain: () => Promise<void>;
  isActing: boolean;
}

// ============================================================================
// CATEGORY DISPLAY CONFIG
// ============================================================================

const CATEGORY_CONFIG: Record<
  SuggestionCategory,
  { label: string; color: string; description: string }
> = {
  [SuggestionCategory.APPROVAL]: {
    label: 'Approval',
    color: 'blue',
    description: 'Rules for approval workflows and decision gates',
  },
  [SuggestionCategory.ESCALATION]: {
    label: 'Escalation',
    color: 'red',
    description: 'Rules for escalating issues based on conditions',
  },
  [SuggestionCategory.VALIDATION]: {
    label: 'Validation',
    color: 'teal',
    description: 'Data validation and integrity rules',
  },
  [SuggestionCategory.NOTIFICATION]: {
    label: 'Notification',
    color: 'violet',
    description: 'Alert and notification trigger rules',
  },
  [SuggestionCategory.ROUTING]: {
    label: 'Routing',
    color: 'orange',
    description: 'Workflow routing and assignment rules',
  },
  [SuggestionCategory.COMPLIANCE]: {
    label: 'Compliance',
    color: 'green',
    description: 'Regulatory compliance and audit rules',
  },
  [SuggestionCategory.PERFORMANCE]: {
    label: 'Performance',
    color: 'cyan',
    description: 'Performance optimization rules',
  },
};

const CATEGORY_OPTIONS = Object.entries(CATEGORY_CONFIG).map(([value, config]) => ({
  value,
  label: config.label,
}));

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

const SuggestionCard: React.FC<SuggestionCardProps> = ({
  suggestion,
  onAccept,
  onReject,
  onExplain,
  isActing,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [explanation, setExplanation] = useState<string | null>(null);
  const [loadingExplanation, setLoadingExplanation] = useState(false);

  const categoryConfig = CATEGORY_CONFIG[suggestion.category];
  const confidenceColor = getConfidenceColor(suggestion.confidence);
  const confidencePercent = Math.round(suggestion.confidence * 100);

  const handleExplain = async () => {
    if (explanation) {
      // Toggle off if already showing
      setExplanation(null);
      return;
    }
    setLoadingExplanation(true);
    try {
      await onExplain();
      // In a real implementation, this would set the explanation from the response
      setExplanation(
        `This rule is suggested because it addresses common patterns in ${suggestion.category.toLowerCase()} workflows. ` +
          `Based on analysis of your existing rules and workflow patterns, this rule would help enforce consistent behavior ` +
          `for scenarios matching the specified conditions.`,
      );
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to get explanation',
        color: 'red',
      });
    } finally {
      setLoadingExplanation(false);
    }
  };

  return (
    <Card shadow="sm" p="md" radius="md" withBorder>
      <Stack gap="sm">
        {/* Header */}
        <Group justify="space-between" ta="left">
          <Group gap="sm">
            <Badge color={categoryConfig.color} variant="filled" size="sm">
              {categoryConfig.label}
            </Badge>
            <Text fw={600} size="md">
              {suggestion.ruleName}
            </Text>
          </Group>

          <Group gap="xs">
            <Tooltip label={`${confidencePercent}% confidence`}>
              <Badge color={confidenceColor} variant="light">
                {confidencePercent}%
              </Badge>
            </Tooltip>
            <Tooltip label={`Estimated impact: ${formatImpact(suggestion.estimatedImpact)}`}>
              <Badge color="gray" variant="outline">
                {formatImpact(suggestion.estimatedImpact)}
              </Badge>
            </Tooltip>
          </Group>
        </Group>

        {/* Confidence Progress Bar */}
        <Progress value={confidencePercent} color={confidenceColor} size="xs" />

        {/* Description */}
        <Text size="sm" c="dimmed">
          {suggestion.description}
        </Text>

        {/* Reasoning */}
        <Paper p="xs" bg="gray.0" radius="sm">
          <Group gap="xs" ta="left">
            <IconBrain size={16} color="gray" />
            <Text size="xs" c="dimmed" style={{ flex: 1 }}>
              {suggestion.reasoning}
            </Text>
          </Group>
        </Paper>

        {/* Expandable Rule Details */}
        <Button
          variant="subtle"
          size="xs"
          onClick={() => setExpanded(!expanded)}
          rightSection={expanded ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
        >
          {expanded ? 'Hide' : 'Show'} Rule Details
        </Button>

        <Collapse in={expanded}>
          <Stack gap="xs" pt="xs">
            {/* Conditions */}
            <Text size="sm" fw={500}>
              Conditions:
            </Text>
            {suggestion.rule.conditions.map((condition, idx) => (
              <Code key={idx} block>
                {condition.field} {condition.operator} {condition.value}
              </Code>
            ))}

            {/* Actions */}
            <Text size="sm" fw={500} mt="xs">
              Actions:
            </Text>
            {suggestion.rule.actions.map((action, idx) => (
              <Code key={idx} block>
                {action.actionType}: {action.params}
              </Code>
            ))}

            {/* Priority & Terminal */}
            <Group gap="xl" mt="xs">
              <Text size="xs" c="dimmed">
                Priority: {suggestion.rule.priority ?? 100}
              </Text>
              <Text size="xs" c="dimmed">
                Terminal: {suggestion.rule.terminal ? 'Yes' : 'No'}
              </Text>
            </Group>
          </Stack>
        </Collapse>

        {/* AI Explanation */}
        {explanation && (
          <Alert
            icon={<IconInfoCircle size={16} />}
            title="AI Explanation"
            color="blue"
            variant="light"
          >
            {explanation}
          </Alert>
        )}

        <Divider />

        {/* Actions */}
        <Group justify="space-between">
          <Button
            variant="subtle"
            size="xs"
            leftSection={loadingExplanation ? <Loader size={14} /> : <IconInfoCircle size={14} />}
            onClick={handleExplain}
            disabled={loadingExplanation}
          >
            {explanation ? 'Hide Explanation' : 'Explain'}
          </Button>

          <Group gap="xs">
            <Button
              variant="light"
              color="red"
              size="sm"
              leftSection={<IconX size={16} />}
              onClick={onReject}
              loading={isActing}
            >
              Reject
            </Button>
            <Button
              variant="filled"
              color="green"
              size="sm"
              leftSection={<IconCheck size={16} />}
              onClick={onAccept}
              loading={isActing}
            >
              Accept
            </Button>
          </Group>
        </Group>

        {/* Metadata Footer */}
        <Group gap="xs" justify="flex-end">
          <Text size="xs" c="dimmed">
            Generated by {suggestion.generatedBy}
          </Text>
          <Text size="xs" c="dimmed">
            {new Date(suggestion.createdAt).toLocaleDateString()}
          </Text>
        </Group>
      </Stack>
    </Card>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const RuleSuggestionReviewer: React.FC<RuleSuggestionReviewerProps> = ({
  tenantId,
  onRuleAccepted,
  onRuleRejected,
}) => {
  // State for generation settings
  const [generationSettings, setGenerationSettings] = useState<
    Omit<SuggestionContextInput, 'tenantId'>
  >({
    focusAreas: [],
    maxSuggestions: 5,
    minConfidence: 0.7,
  });

  // Modal state for generation settings
  const [settingsOpened, { open: openSettings, close: closeSettings }] = useDisclosure(false);

  // Hook for suggestion review workflow
  const {
    suggestions,
    byCategory,
    sortedByConfidence,
    count,
    generateNew,
    accept,
    reject,
    clear,
    getExplanation,
    refetch,
    loading,
    isGenerating,
    isActing,
  } = useSuggestionReview(tenantId);

  // Handle generating new suggestions
  const handleGenerate = useCallback(async () => {
    try {
      await generateNew(generationSettings);
      notifications.show({
        title: 'Suggestions Generated',
        message: 'New rule suggestions have been generated',
        color: 'green',
        icon: <IconSparkles size={16} />,
      });
      closeSettings();
    } catch {
      notifications.show({
        title: 'Generation Failed',
        message: 'Failed to generate suggestions. Please try again.',
        color: 'red',
      });
    }
  }, [generateNew, generationSettings, closeSettings]);

  // Handle accepting a suggestion
  const handleAccept = useCallback(
    async (suggestion: SuggestedRule) => {
      try {
        await accept(suggestion.suggestionId);
        notifications.show({
          title: 'Rule Accepted',
          message: `"${suggestion.ruleName}" has been accepted and will be converted to an active rule`,
          color: 'green',
          icon: <IconCheck size={16} />,
        });
        onRuleAccepted?.(suggestion);
      } catch {
        notifications.show({
          title: 'Accept Failed',
          message: 'Failed to accept the suggestion',
          color: 'red',
        });
      }
    },
    [accept, onRuleAccepted],
  );

  // Handle rejecting a suggestion
  const handleReject = useCallback(
    async (suggestion: SuggestedRule) => {
      try {
        await reject(suggestion.suggestionId);
        notifications.show({
          title: 'Rule Rejected',
          message: `"${suggestion.ruleName}" has been rejected`,
          color: 'orange',
          icon: <IconX size={16} />,
        });
        onRuleRejected?.(suggestion);
      } catch {
        notifications.show({
          title: 'Reject Failed',
          message: 'Failed to reject the suggestion',
          color: 'red',
        });
      }
    },
    [reject, onRuleRejected],
  );

  // Handle clearing all suggestions
  const handleClear = useCallback(async () => {
    try {
      await clear();
      notifications.show({
        title: 'Suggestions Cleared',
        message: 'All pending suggestions have been cleared',
        color: 'blue',
        icon: <IconTrash size={16} />,
      });
    } catch {
      notifications.show({
        title: 'Clear Failed',
        message: 'Failed to clear suggestions',
        color: 'red',
      });
    }
  }, [clear]);

  // Handle explaining a suggestion
  const handleExplain = useCallback(
    async (suggestionId: string) => {
      await getExplanation(suggestionId);
    },
    [getExplanation],
  );

  return (
    <Stack gap="md">
      {/* Header */}
      <Group justify="space-between">
        <Group gap="sm">
          <IconRobot size={28} />
          <div>
            <Title order={3}>AI Rule Suggestions</Title>
            <Text size="sm" c="dimmed">
              Review and approve LLM-generated policy rules
            </Text>
          </div>
        </Group>

        <Group gap="xs">
          <Button
            variant="light"
            leftSection={<IconRefresh size={16} />}
            onClick={() => refetch()}
            loading={loading}
          >
            Refresh
          </Button>
          {count > 0 && (
            <Button
              variant="subtle"
              color="red"
              leftSection={<IconTrash size={16} />}
              onClick={handleClear}
              disabled={isActing}
            >
              Clear All
            </Button>
          )}
          <Button
            variant="filled"
            leftSection={<IconSparkles size={16} />}
            onClick={openSettings}
            loading={isGenerating}
          >
            Generate Suggestions
          </Button>
        </Group>
      </Group>

      {/* Stats Bar */}
      <Paper p="sm" withBorder>
        <Group justify="space-around">
          <div style={{ textAlign: 'center' }}>
            <Text size="xl" fw={700}>
              {count}
            </Text>
            <Text size="xs" c="dimmed">
              Pending
            </Text>
          </div>
          {Object.entries(byCategory).map(
            ([category, rules]) =>
              rules.length > 0 && (
                <div key={category} style={{ textAlign: 'center' }}>
                  <Badge color={CATEGORY_CONFIG[category as SuggestionCategory].color}>
                    {rules.length}
                  </Badge>
                  <Text size="xs" c="dimmed">
                    {CATEGORY_CONFIG[category as SuggestionCategory].label}
                  </Text>
                </div>
              ),
          )}
        </Group>
      </Paper>

      {/* Suggestions List */}
      {loading && !suggestions.length ? (
        <Paper p="xl" withBorder style={{ textAlign: 'center' }}>
          <Loader size="lg" />
          <Text mt="md" c="dimmed">
            Loading suggestions...
          </Text>
        </Paper>
      ) : count === 0 ? (
        <Paper p="xl" withBorder style={{ textAlign: 'center' }}>
          <IconRobot size={48} color="gray" />
          <Title order={4} mt="md">
            No Pending Suggestions
          </Title>
          <Text c="dimmed" mt="xs">
            Click &quot;Generate Suggestions&quot; to have AI analyze your workflow and suggest new
            rules
          </Text>
          <Button mt="md" leftSection={<IconSparkles size={16} />} onClick={openSettings}>
            Generate Suggestions
          </Button>
        </Paper>
      ) : (
        <Tabs defaultValue="all">
          <Tabs.List>
            <Tabs.Tab value="all">All ({count})</Tabs.Tab>
            <Tabs.Tab value="high-confidence">High Confidence</Tabs.Tab>
            <Tabs.Tab value="by-category">By Category</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="all" pt="md">
            <ScrollArea h={600}>
              <Stack gap="md">
                {sortedByConfidence.map((suggestion) => (
                  <SuggestionCard
                    key={suggestion.suggestionId}
                    suggestion={suggestion}
                    onAccept={() => handleAccept(suggestion)}
                    onReject={() => handleReject(suggestion)}
                    onExplain={() => handleExplain(suggestion.suggestionId)}
                    isActing={isActing}
                  />
                ))}
              </Stack>
            </ScrollArea>
          </Tabs.Panel>

          <Tabs.Panel value="high-confidence" pt="md">
            <ScrollArea h={600}>
              <Stack gap="md">
                {sortedByConfidence
                  .filter((s) => s.confidence >= 0.8)
                  .map((suggestion) => (
                    <SuggestionCard
                      key={suggestion.suggestionId}
                      suggestion={suggestion}
                      onAccept={() => handleAccept(suggestion)}
                      onReject={() => handleReject(suggestion)}
                      onExplain={() => handleExplain(suggestion.suggestionId)}
                      isActing={isActing}
                    />
                  ))}
              </Stack>
            </ScrollArea>
          </Tabs.Panel>

          <Tabs.Panel value="by-category" pt="md">
            <SimpleGrid cols={{ base: 2 }}>
              {Object.entries(byCategory).map(
                ([category, rules]) =>
                  rules.length > 0 && (
                    <Card key={category} withBorder>
                      <Title order={5} mb="sm">
                        {CATEGORY_CONFIG[category as SuggestionCategory].label}
                      </Title>
                      <Text size="xs" c="dimmed" mb="md">
                        {CATEGORY_CONFIG[category as SuggestionCategory].description}
                      </Text>
                      <Stack gap="sm">
                        {rules.map((suggestion) => (
                          <Paper key={suggestion.suggestionId} p="xs" withBorder>
                            <Group justify="space-between">
                              <Text size="sm">{suggestion.ruleName}</Text>
                              <Group gap="xs">
                                <ActionIcon
                                  variant="light"
                                  color="red"
                                  size="sm"
                                  onClick={() => handleReject(suggestion)}
                                >
                                  <IconX size={14} />
                                </ActionIcon>
                                <ActionIcon
                                  variant="filled"
                                  color="green"
                                  size="sm"
                                  onClick={() => handleAccept(suggestion)}
                                >
                                  <IconCheck size={14} />
                                </ActionIcon>
                              </Group>
                            </Group>
                          </Paper>
                        ))}
                      </Stack>
                    </Card>
                  ),
              )}
            </SimpleGrid>
          </Tabs.Panel>
        </Tabs>
      )}

      {/* Generation Settings Modal */}
      <Modal opened={settingsOpened} onClose={closeSettings} title="Generate AI Suggestions">
        <Stack gap="md">
          <MultiSelect
            label="Focus Areas"
            description="Select specific rule categories to focus on"
            placeholder="All categories"
            data={CATEGORY_OPTIONS}
            value={generationSettings.focusAreas ?? []}
            onChange={(value) =>
              setGenerationSettings({
                ...generationSettings,
                focusAreas: value as SuggestionCategory[],
              })
            }
          />

          <NumberInput
            label="Maximum Suggestions"
            description="Number of suggestions to generate"
            min={1}
            max={20}
            value={generationSettings.maxSuggestions}
            onChange={(value) =>
              setGenerationSettings({
                ...generationSettings,
                maxSuggestions: typeof value === 'number' ? value : 5,
              })
            }
          />

          <NumberInput
            label="Minimum Confidence"
            description="Only show suggestions above this confidence level"
            min={0}
            max={1}
            step={0.1}
            decimalScale={1}
            value={generationSettings.minConfidence}
            onChange={(value) =>
              setGenerationSettings({
                ...generationSettings,
                minConfidence: typeof value === 'number' ? value : 0.7,
              })
            }
          />

          <Alert icon={<IconBrain size={16} />} color="blue" variant="light">
            AI will analyze your existing rules and workflow patterns to suggest new rules that
            could improve your system.
          </Alert>

          <Button
            fullWidth
            leftSection={<IconSparkles size={16} />}
            onClick={handleGenerate}
            loading={isGenerating}
          >
            Generate Suggestions
          </Button>
        </Stack>
      </Modal>
    </Stack>
  );
};

export default RuleSuggestionReviewer;
